import ApiConstant from "./ApiContstant";
import DataService from "./DataService";
import { Resource } from './SearchService';

export interface GetUserBookingRequest {
  //Either Id or EmailAddress can be used but at least one is required
  Id?: number[];
  EmailAddress?: string;
  Date?: string; // Optional: Defaults to the current day
  Location?: {
    // Optional
    LocationId: number; // Optional: Location ID if available
    Country: string;
    City: string;
    Site: string;
    Building: string;
    Floor: string;
    Zone: string;
  };
}

export interface UserBooking {
  BookingId: number; // The unique booking ID number
  AllDay: boolean; // Is it an all day booking 0 = No 1 = Yes
  Start: string; // Start time for the booking in 24hr
  StartIn: number; // Nunber of minutes until the booking starts
  End: string; // End time for the booking in 24hr
  EndsIn: number; // Number of minutes until the booking ends
  Setup: string;
  Cleardown: string;
  StartTimeOriginal: string;
  Subject: string; // Booking subject
  Description: string; // Booking description
  Organiser: {
    Id: number; // ID number for the organiser
    FirstName: string;
    LastName: string;
    EmailAddress: string;
    UserImage: string; // Path to current user image
  };
  Attendees: [
    // An array of user objects for all attendees for the booking.
    {
      Id: number; // ID number for the organiser
      FirstName: string;
      LastName: string;
      EmailAddress: string;
      UserImage: string; // Path to current user image
    }
  ];
  BookedUser: {
    // Object for the user who created the booking
    Id: number; // ID number for the booking user
    FirstName: string;
    LastName: string;
    EmailAddress: string;
    UserImage: string; // Path to current user image
  };
  Resource: {
    Id: number; // DB ID for the resource profile
    ResId: number; // User facing ID number for resource profile
    Name: string; // Resource display name
    SpaceType: string;
    RoomImg: string;
    Occ: number;
    Description: string;
    Keywords: [string];
    Equips: [
      {
        ResourceId: number;
        EquipmentId: number;
        ImagePath: string;
        Name: string;
        FaultImagePath: string;
      }
    ];
    Location: {
      LocationId: number;
      Country: string;
      City: string;
      Site: string;
      Building: string;
      Floor: string;
      Zone: string;
    };
    MapID: number;
    SetupDuration: number;
    CleardownDuration: number;
    LastUsedOrLastCleaned: string;
    CheckInMandatorySpaceUsageAcceptanceText: string;
  };
  MeetingStatus: string;
  Actions: {
    Cancel: boolean;
    CheckIn: boolean;
    CheckOut: boolean;
    Extend: boolean;
  };
  ParentBookingId: number;
  RecurringOption: null;
  MeetingText: string;
  RingFenceBookingId: string;
}

export interface SpaceKeyword {
  ResourceId: number;
  Keyword: string;
}

export interface SpaceEquipment {
  ResourceId: number;
  EquipmentId: number;
  Name: string;
  ImagePath: string;
  FaultImagePath?: string;
}

export interface SpaceDetail {
  Id: number;
  ResID: number;
  ResName: string | null;
  Status: number;
  TitleMandatory: boolean;
  RoomImage: string;
  Occupancy: number;
  Description: string;
  Keywords: SpaceKeyword[];
  Equipment: SpaceEquipment[];
  Location: string | null;
  SpaceType: string;
  SpaceTypeIcon: string | null;
  MapID: number;
  SetupDuration: number;
  CleardownDuration: number;
  LastUsedOrLastCleaned: string | null;
  IsRecurringBookingEnabled: boolean;
  BookMandatorySpaceUsageAcceptanceText: string;
  CheckInMandatorySpaceUsageAcceptanceText: string;
}

export interface BookingCheckInRequest {
  bookid: number;
  loginuser: string;
  loginusername: string;
  resid: number;
}

export interface BookingCheckOutRequest {
  iscleardownaction: number;
  bookid: number;
  loginuser: string;
  loginusername: string;
  resid: number;
}

export interface CancelBookingRequest {
  bookingid: number;
  parentBookingId: number | null;
  userid: string
}

export interface ExtendBookingRequest {
  addmins: string;
  bookid: number;
  loginuser: string;
  loginusername: string;
  resid: number;
  extendedfrom: number | null;
}

export interface ResourceAvailabilityCheckRequest {
  ResId: number;
  CheckDateTime: DateTimeObject[];
}

export interface DateTimeObject {
  StartDateTime: string;
  EndDateTime: string;
}

export interface ResourceAvailabilityCheckResponse {
  ResId: number;
  UnavailableDateTime: DateTimeObject[];
}

export interface SelectedResourceObject {
  resource: Resource;
  availablity: boolean;
  unavailableDateTime: DateTimeObject[] | null;
}

export interface BookOption {
  name: string;
  id: number;
  code: string;
  bookIndex: string;
}

export interface GetResourceDataRequest {
  islocal: boolean;
  requestdate: string;
  resid: number;
}

export interface GetResourceDataResponse {
  ServerTime: string;
  ServerDate: string;
  ServerDate1: string;
  SystemSettings: {
    AMStart: string;
    AMFinish: string;
    PMStart: string;
    PMFinish: string;
  }
}

export interface CreateBookingRequest {
  Title: string;
  Private: boolean;
  BookOption: string; // AM, PM, EOD, WD, AD
  StartDateTime: string; //DD/MM/YYYY HH:mm
  EndDateTime: string; //DD/MM/YYYY HH:mm
  Recurring: number;
  Description: string;
  User: CreateBookingUserObject;
  Organiser: CreateBookingUserObject;
  Attendees: CreateBookingUserObject[];
  Resources: number[];
}

export interface CreateBookingResponse {
  ErrCode: string;
  ErrDesc: string;
  Booked: boolean;
  Response: boolean;
}
export interface CreateBookingUserObject {
  Id: number;
  EmailAddress: string;
}

export interface CreateBookingResponse {}
class UserBookingService {
  static readonly bookOptions: BookOption[] = [
    {
      name: "Custom",
      id: 0,
      code: "Custom",
      bookIndex: "0",
    },
    {
      name: "15 mins",
      id: 1,
      code: "15",
      bookIndex: "1",
    },
    {
      name: "30 mins",
      id: 2,
      code: "30",
      bookIndex: "2",
    },
    {
      name: "1 hr",
      id: 3,
      code: "60",
      bookIndex: "3",
    },
    {
      name: "1 hr 30 mins",
      id: 4,
      code: "90",
      bookIndex: "4",
    },
    {
      name: "2 hrs",
      id: 5,
      code: "120",
      bookIndex: "5",
    },
    {
      name: "3 hrs",
      id: 6,
      code: "180",
      bookIndex: "6",
    },
    {
      name: "4 hrs",
      id: 7,
      code: "240",
      bookIndex: "7",
    },
    {
      name: "6 hrs",
      id: 8,
      code: "360",
      bookIndex: "8",
    },
    {
      name: "8 hrs",
      id: 9,
      code: "480",
      bookIndex: "9",
    },
    {
      name: "AM",
      id: 10,
      code: "AM",
      bookIndex: "10",
    },
    {
      name: "PM",
      id: 11,
      code: "PM",
      bookIndex: "11",
    },
    {
      name: "End of day",
      id: 12,
      code: "EOD",
      bookIndex: "12",
    },
    {
      name: "Working Day",
      id: 14,
      code: "WD",
      bookIndex: "14",
    },
    {
      name: "All day",
      id: 15,
      code: "AD",
      bookIndex: "15",
    },
    {
      name: "more",
      id: 13,
      code: "more",
      bookIndex: "13",
    },
  ]

  async getUserBookings(
    payload: GetUserBookingRequest
  ): Promise<UserBooking[]> {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.GetUserBookings,
      payload
    );

    return response?.data?.d ?? [];
  }

  async BookingCheckIn(
    payload: BookingCheckInRequest
  ) {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.BookingCheckIn,
      payload
    );

    return response?.data?.d ?? {};
  }

  async BookingCheckOut(
    payload: BookingCheckOutRequest
  ) {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.BookingCheckOut,
      payload
    );

    return response?.data?.d ?? {};
  }

  async BookingExtend(
    payload: ExtendBookingRequest
  ) {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.BookingExtend,
      payload
    );

    return response?.data?.d ?? {};
  }

  async BookingCancel(
    payload: CancelBookingRequest
  ) {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.BookingCancel,
      payload
    );

    return response?.data?.d ?? {};
  }

  async checkResourceAvailability(
    payload: ResourceAvailabilityCheckRequest
  ): Promise<ResourceAvailabilityCheckResponse> {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.ResourceAvailabilityCheck,
      payload
    );

    return response?.data?.d ?? {};
  }

  async getResourceData(
    payload: GetResourceDataRequest
  ): Promise<GetResourceDataResponse> {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.GetResourceData,
      payload
    );

    const data = response?.data?.d;
    const mappedResponse: GetResourceDataResponse = {
      ServerTime: data.ServerTime,
      ServerDate: data.ServerDate,
      ServerDate1: data.ServerDate1,
      SystemSettings: {
        AMStart: data.SystemSettings.AMStart,
        AMFinish: data.SystemSettings.AMFinish,
        PMStart: data.SystemSettings.PMStart,
        PMFinish: data.SystemSettings.PMFinish
      }
    }

    return mappedResponse;
  }

  async createBooking(
    payload: CreateBookingRequest
  ): Promise<CreateBookingResponse> {
    const response = await DataService.postData(
      localStorage.getItem("tenantUrl") + ApiConstant.CreateBooking,
      payload
    );

    return response?.data?.d ?? {};
  }
}

export default new UserBookingService;
